import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, MenuItem, TextField,
  Button, CircularProgress, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, Stack, Alert, Paper, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Tooltip, Tab, Tabs, InputAdornment,
  FormControlLabel, Checkbox, FormGroup, FormLabel
} from '@mui/material';
import {
  Gavel, Add, Edit, Delete, Search, Refresh,
  Warning, CheckCircle, Schedule, Person,
  ReportProblem, Block, BarChart, FilterList
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { settingsAPI } from '../services/api';
import { exportCSV } from '../utils/export';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const api = axios.create({ baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api' });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('sms_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// ── Config ─────────────────────────────────────────────────────────────────────
const INCIDENT_TYPES = [
  { value: 'misconduct',          label: 'Misconduct / Behaviour' },
  { value: 'fighting',            label: 'Fighting / Violence' },
  { value: 'bullying',            label: 'Bullying' },
  { value: 'truancy',             label: 'Truancy / Absenteeism' },
  { value: 'vandalism',           label: 'Vandalism' },
  { value: 'substance_abuse',     label: 'Substance Abuse' },
  { value: 'academic_dishonesty', label: 'Cheating / Academic Dishonesty' },
  { value: 'uniform_violation',   label: 'Uniform Violation' },
  { value: 'insubordination',     label: 'Insubordination' },
  { value: 'other',               label: 'Other (specify)' },
];

const ACTIONS = [
  { value: 'verbal_warning',   label: 'Verbal Warning' },
  { value: 'written_warning',  label: 'Written Warning' },
  { value: 'detention',        label: 'Detention' },
  { value: 'parent_called',    label: 'Parent Called' },
  { value: 'suspension_in',    label: 'Suspension (In-School)' },
  { value: 'suspension_out',   label: 'Suspension (Out-of-School)' },
  { value: 'expulsion',        label: 'Expulsion' },
  { value: 'community_service','label': 'Community Service' },
  { value: 'counselling',      label: 'Counselling Referred' },
  { value: 'no_action',        label: 'No Action Taken' },
];

const SEVERITY_CFG = {
  minor:    { color: 'default', bg: '#f5f5f5', text: 'Minor' },
  moderate: { color: 'warning', bg: '#fff8e1', text: 'Moderate' },
  serious:  { color: 'error',   bg: '#fff3f3', text: 'Serious' },
  critical: { color: 'error',   bg: '#fce4e4', text: 'Critical' },
};

const itLabel = v => INCIDENT_TYPES.find(t => t.value === v)?.label || v;
const acLabel = v => v ? v.split(',').map(a => ACTIONS.find(x => x.value === a)?.label || a).join(', ') : '—';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 3 }}>{children}</Box> : null;
}

// ── Incident Form Dialog ───────────────────────────────────────────────────────
function IncidentDialog({ open, onClose, onSave, editing, students, academicYears }) {
  const BLANK = {
    student_id: '', academic_year_id: '', incident_date: new Date().toISOString().split('T')[0],
    incident_type: 'misconduct', other_description: '', severity: 'minor',
    description: '', location: '', witnesses: '',
    action_taken: ['verbal_warning'],
    suspension_days: 0, suspension_start: '', suspension_end: '',
    parent_notified: false, follow_up_required: false,
    follow_up_notes: '', notes: '', resolved: false
  };
  const [form, setForm] = useState(BLANK);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target?.value ?? e }));

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        ...editing,
        incident_date: editing.incident_date ? editing.incident_date.split('T')[0] : new Date().toISOString().split('T')[0],
        action_taken: editing.action_taken ? editing.action_taken.split(',') : ['verbal_warning'],
        parent_notified: !!editing.parent_notified,
        follow_up_required: !!editing.follow_up_required,
        resolved: !!editing.resolved,
        suspension_start: editing.suspension_start?.split('T')[0] || '',
        suspension_end: editing.suspension_end?.split('T')[0] || '',
        follow_up_notes: editing.follow_up_notes || '',
        notes: editing.notes || '',
      });
    } else {
      const cur = academicYears.find(y => y.is_current);
      setForm({ ...BLANK, academic_year_id: cur?.id || '' });
    }
  }, [open, editing]);

  const toggleAction = (val) => {
    setForm(f => ({
      ...f,
      action_taken: f.action_taken.includes(val)
        ? f.action_taken.filter(a => a !== val)
        : [...f.action_taken, val]
    }));
  };

  const hasSuspension = form.action_taken?.includes('suspension_in') || form.action_taken?.includes('suspension_out');
  const isOther = form.incident_type === 'other';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ bgcolor: '#0f2b4a', color: 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Gavel /> {editing ? 'Edit Incident Record' : 'Log Disciplinary Incident'}
      </DialogTitle>
      <DialogContent dividers sx={{ p: 3 }}>
        <Grid container spacing={2}>
          {/* Student & Date */}
          <Grid item xs={12} sm={6}>
            <TextField select fullWidth size="small" label="Student *" value={form.student_id} onChange={set('student_id')}>
              <MenuItem value="">Select student…</MenuItem>
              {students.map(s => <MenuItem key={s.id} value={s.id}>{s.first_name} {s.last_name} ({s.admission_number})</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField select fullWidth size="small" label="Academic Year *" value={form.academic_year_id} onChange={set('academic_year_id')}>
              {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField fullWidth size="small" type="date" label="Incident Date *" value={form.incident_date} onChange={set('incident_date')} InputLabelProps={{ shrink: true }} />
          </Grid>

          {/* Incident Type & Severity */}
          <Grid item xs={12} sm={isOther ? 4 : 6}>
            <TextField select fullWidth size="small" label="Incident Type *" value={form.incident_type} onChange={set('incident_type')}>
              {INCIDENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
            </TextField>
          </Grid>
          {isOther && (
            <Grid item xs={12} sm={4}>
              <TextField fullWidth size="small" label="Specify incident type *" value={form.other_description} onChange={set('other_description')} />
            </Grid>
          )}
          <Grid item xs={12} sm={isOther ? 4 : 6}>
            <TextField select fullWidth size="small" label="Severity *" value={form.severity} onChange={set('severity')}>
              <MenuItem value="minor"><Chip label="Minor" size="small" sx={{ mr: 1 }} />Minor — small rule infraction</MenuItem>
              <MenuItem value="moderate"><Chip label="Moderate" color="warning" size="small" sx={{ mr: 1 }} />Moderate — repeated or disruptive</MenuItem>
              <MenuItem value="serious"><Chip label="Serious" color="error" size="small" sx={{ mr: 1 }} />Serious — harmful or threatening</MenuItem>
              <MenuItem value="critical"><Chip label="Critical" color="error" size="small" sx={{ mr: 1 }} />Critical — immediate risk</MenuItem>
            </TextField>
          </Grid>

          {/* Location */}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Location" value={form.location} onChange={set('location')} placeholder="e.g. Classroom 3B, Sports field, Canteen" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth size="small" label="Witnesses" value={form.witnesses} onChange={set('witnesses')} placeholder="Names of staff or students who witnessed" />
          </Grid>

          {/* Description */}
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Incident Description *" value={form.description} onChange={set('description')}
              multiline rows={3} placeholder="Describe the incident in detail — what happened, who was involved, what was said/done…" />
          </Grid>

          {/* Actions */}
          <Grid item xs={12}>
            <FormLabel sx={{ fontSize: 12, fontWeight: 700, color: '#0f2b4a' }}>Actions Taken *</FormLabel>
            <FormGroup row sx={{ mt: 0.5 }}>
              {ACTIONS.map(a => (
                <FormControlLabel key={a.value} control={
                  <Checkbox size="small" checked={form.action_taken?.includes(a.value) || false}
                    onChange={() => toggleAction(a.value)} />
                } label={<Typography fontSize={12}>{a.label}</Typography>} />
              ))}
            </FormGroup>
          </Grid>

          {/* Suspension fields */}
          {hasSuspension && (
            <>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="number" label="Suspension Days" value={form.suspension_days} onChange={set('suspension_days')} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="date" label="Suspension Start" value={form.suspension_start} onChange={set('suspension_start')} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={4}>
                <TextField fullWidth size="small" type="date" label="Suspension End" value={form.suspension_end} onChange={set('suspension_end')} InputLabelProps={{ shrink: true }} />
              </Grid>
            </>
          )}

          {/* Status flags — prominent section */}
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#f5f6fa', border: '1px solid #c9d4e0' }}>
              <Typography fontSize={12} fontWeight={700} color="#0f2b4a" sx={{ mb: 1.5 }}>📋 Incident Status</Typography>
              <Stack direction="row" spacing={2} flexWrap="wrap">
                <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, border: `2px solid ${form.parent_notified ? '#2e7d32' : '#ddd'}`, bgcolor: form.parent_notified ? '#f0fdf4' : 'white', cursor: 'pointer' }}
                  onClick={() => setForm(f => ({ ...f, parent_notified: !f.parent_notified }))}>
                  <FormControlLabel control={
                    <Checkbox size="small" checked={form.parent_notified} onChange={e => setForm(f => ({ ...f, parent_notified: e.target.checked }))}
                      sx={{ color: '#2e7d32', '&.Mui-checked': { color: '#2e7d32' } }} />
                  } label={<Typography fontSize={13} fontWeight={600} color={form.parent_notified ? '#2e7d32' : 'text.primary'}>
                    📞 Parent / Guardian Notified
                  </Typography>} sx={{ m: 0 }} />
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, border: `2px solid ${form.follow_up_required ? '#e65100' : '#ddd'}`, bgcolor: form.follow_up_required ? '#fff8f0' : 'white', cursor: 'pointer' }}
                  onClick={() => setForm(f => ({ ...f, follow_up_required: !f.follow_up_required }))}>
                  <FormControlLabel control={
                    <Checkbox size="small" checked={form.follow_up_required} onChange={e => setForm(f => ({ ...f, follow_up_required: e.target.checked }))}
                      sx={{ color: '#e65100', '&.Mui-checked': { color: '#e65100' } }} />
                  } label={<Typography fontSize={13} fontWeight={600} color={form.follow_up_required ? '#e65100' : 'text.primary'}>
                    ⏰ Follow-up Required
                  </Typography>} sx={{ m: 0 }} />
                </Paper>
                <Paper variant="outlined" sx={{ px: 2, py: 1, borderRadius: 2, border: `2px solid ${form.resolved ? '#1565c0' : '#ddd'}`, bgcolor: form.resolved ? '#e8f0fe' : 'white', cursor: 'pointer' }}
                  onClick={() => setForm(f => ({ ...f, resolved: !f.resolved }))}>
                  <FormControlLabel control={
                    <Checkbox size="small" checked={form.resolved} onChange={e => setForm(f => ({ ...f, resolved: e.target.checked }))}
                      sx={{ color: '#1565c0', '&.Mui-checked': { color: '#1565c0' } }} />
                  } label={<Typography fontSize={13} fontWeight={600} color={form.resolved ? '#1565c0' : 'text.primary'}>
                    ✅ Mark as Resolved / Closed
                  </Typography>} sx={{ m: 0 }} />
                </Paper>
              </Stack>
            </Paper>
          </Grid>

          {form.follow_up_required && (
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Follow-up Notes" value={form.follow_up_notes} onChange={set('follow_up_notes')} multiline rows={2} />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Additional Notes (internal)" value={form.notes} onChange={set('notes')} multiline rows={2} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => onSave(form)} startIcon={<Gavel />} sx={{ bgcolor: '#0f2b4a' }}>
          {editing ? 'Update Record' : 'Log Incident'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Overview / Stats Tab ───────────────────────────────────────────────────────
function StatsTab({ academicYears }) {
  const { enqueueSnackbar } = useSnackbar();
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(false);
  const [selectedYear, setYear] = useState('');

  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);
  useEffect(() => {
    if (!selectedYear) return;
    setLoading(true);
    api.get('/disciplinary/stats', { params: { academic_year_id: selectedYear } })
      .then(r => { if (r.data.success) setStats(r.data.data); })
      .catch(() => enqueueSnackbar('Failed to load stats', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, [selectedYear, enqueueSnackbar]);

  const s = stats?.summary;
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <TextField select size="small" label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)} sx={{ minWidth: 200 }}>
          {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
        </TextField>
      </Box>

      {loading ? <CircularProgress /> : s && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Total Incidents', value: s.total, color: '#0f2b4a' },
              { label: 'Open / Unresolved', value: s.open, color: '#c62828' },
              { label: 'Resolved', value: s.resolved, color: '#2e7d32' },
              { label: 'Suspensions', value: s.suspensions, color: '#e65100' },
            ].map(card => (
              <Grid item xs={6} sm={3} key={card.label}>
                <Card variant="outlined" sx={{ borderRadius: 2, borderLeft: `4px solid ${card.color}` }}>
                  <CardContent sx={{ pb: '12px !important' }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={600} textTransform="uppercase">{card.label}</Typography>
                    <Typography variant="h4" fontWeight={800} sx={{ color: card.color }}>{card.value || 0}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Severity breakdown */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Minor', value: s.minor, color: 'default' },
              { label: 'Moderate', value: s.moderate, color: 'warning' },
              { label: 'Serious', value: s.serious, color: 'error' },
              { label: 'Critical', value: s.critical, color: 'error' },
            ].map(sv => (
              <Grid item xs={3} key={sv.label}>
                <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', borderRadius: 2 }}>
                  <Chip label={sv.label} color={sv.color} size="small" sx={{ mb: 0.5 }} />
                  <Typography fontWeight={700} fontSize={22}>{sv.value || 0}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>Incidents by Type</Typography>
                  {stats.by_type?.map(bt => (
                    <Box key={bt.incident_type} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography fontSize={13}>{itLabel(bt.incident_type)}</Typography>
                      <Chip label={bt.count} size="small" sx={{ fontWeight: 700 }} />
                    </Box>
                  ))}
                  {!stats.by_type?.length && <Typography fontSize={12} color="text.secondary">No incidents recorded</Typography>}
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography fontWeight={700} gutterBottom>Students with Most Incidents</Typography>
                  {stats.top_students?.map((st, i) => (
                    <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                      <Box>
                        <Typography fontSize={13} fontWeight={600}>{st.first_name} {st.last_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{st.admission_number}</Typography>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <Chip label={`${st.incident_count} total`} size="small" color="primary" sx={{ fontSize: 10 }} />
                        {st.serious_count > 0 && <Chip label={`${st.serious_count} serious`} size="small" color="error" sx={{ fontSize: 10 }} />}
                      </Stack>
                    </Box>
                  ))}
                  {!stats.top_students?.length && <Typography fontSize={12} color="text.secondary">No incidents recorded</Typography>}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
}

// ── Incidents List Tab ─────────────────────────────────────────────────────────
function IncidentsTab({ academicYears, classes, students }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading]     = useState(false);
  const [selectedYear, setYear]   = useState('');
  const [filterClass, setClass]   = useState('');
  const [filterSeverity, setSev]  = useState('');
  const [filterType, setType]     = useState('');
  const [filterResolved, setRes]  = useState('');
  const [search, setSearch]       = useState('');
  const [dialog, setDialog]       = useState(false);
  const [editing, setEditing]     = useState(null);
  const [detail, setDetail]       = useState(null);

  const load = useCallback(async () => {
    if (!selectedYear) return;
    setLoading(true);
    try {
      const r = await api.get('/disciplinary', { params: {
        academic_year_id: selectedYear,
        class_id: filterClass || undefined,
        severity: filterSeverity || undefined,
        incident_type: filterType || undefined,
        resolved: filterResolved !== '' ? filterResolved : undefined,
      }});
      if (r.data.success) setIncidents(r.data.data);
    } catch { enqueueSnackbar('Failed to load incidents', { variant: 'error' }); }
    finally { setLoading(false); }
  }, [selectedYear, filterClass, filterSeverity, filterType, filterResolved, enqueueSnackbar]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const cur = academicYears.find(y => y.is_current); if (cur) setYear(cur.id); }, [academicYears]);

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/disciplinary/${editing.id}`, form);
        enqueueSnackbar('Incident updated', { variant: 'success' });
      } else {
        await api.post('/disciplinary', form);
        enqueueSnackbar('Incident logged', { variant: 'success' });
      }
      setDialog(false); setEditing(null); load();
    } catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this incident record?')) return;
    try { await api.delete(`/disciplinary/${id}`); enqueueSnackbar('Deleted', { variant: 'success' }); load(); }
    catch (err) { enqueueSnackbar(err.response?.data?.message || 'Error', { variant: 'error' }); }
  };

  const filtered = incidents.filter(i => !search ||
    `${i.first_name} ${i.last_name} ${i.admission_number}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box>
      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={2}>
          <TextField select size="small" fullWidth label="Academic Year" value={selectedYear} onChange={e => setYear(e.target.value)}>
            {academicYears.map(y => <MenuItem key={y.id} value={y.id}>{y.year_label} — {y.term}{y.is_current ? ' ✓' : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Class" value={filterClass} onChange={e => setClass(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {classes.map(c => <MenuItem key={c.id} value={c.id}>{c.name}{c.stream ? ` (${c.stream})` : ''}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Severity" value={filterSeverity} onChange={e => setSev(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {['minor','moderate','serious','critical'].map(s => <MenuItem key={s} value={s} sx={{ textTransform: 'capitalize' }}>{s}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Type" value={filterType} onChange={e => setType(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            {INCIDENT_TYPES.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField select size="small" fullWidth label="Status" value={filterResolved} onChange={e => setRes(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="false">Open</MenuItem>
            <MenuItem value="true">Resolved</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={12} sm={2}>
          <TextField size="small" fullWidth placeholder="Search student…" value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
        </Grid>
      </Grid>

      {/* Action bar */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }} alignItems="center">
        <Chip label={`${filtered.length} incident(s)`} size="small" />
        <Chip label={`${filtered.filter(i => !i.resolved).length} open`} size="small" color="error" />
        <Box sx={{ flexGrow: 1 }} />
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><Refresh /></IconButton></Tooltip>
        <Button size="small" variant="outlined" onClick={() => {
          exportCSV('disciplinary_incidents',
            ['Date', 'Student', 'Admission', 'Class', 'Type', 'Severity', 'Action Taken', 'Parent Notified', 'Resolved'],
            filtered.map(i => [fmtDate(i.incident_date), `${i.first_name} ${i.last_name}`, i.admission_number,
              i.class_name||'', itLabel(i.incident_type), i.severity, acLabel(i.action_taken),
              i.parent_notified ? 'Yes' : 'No', i.resolved ? 'Yes' : 'No'])
          );
        }}>Export CSV</Button>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditing(null); setDialog(true); }} sx={{ bgcolor: '#0f2b4a' }}>
          Log Incident
        </Button>
      </Stack>

      {loading ? <CircularProgress /> : (
        <Card variant="outlined" sx={{ borderRadius: 2 }}>
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {['Date', 'Student', 'Class', 'Type', 'Severity', 'Action Taken', 'Parent', 'Status', 'Actions'].map(h => (
                    <TableCell key={h} sx={{ bgcolor: '#0f2b4a', color: 'white', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>No incidents found</TableCell></TableRow>
                )}
                {filtered.map((inc, i) => {
                  const sev = SEVERITY_CFG[inc.severity] || SEVERITY_CFG.minor;
                  return (
                    <TableRow key={inc.id} hover sx={{ bgcolor: inc.resolved ? '#f5fff5' : sev.bg, cursor: 'pointer' }}
                      onClick={() => setDetail(inc)}>
                      <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(inc.incident_date)}</TableCell>
                      <TableCell>
                        <Typography fontSize={12} fontWeight={600}>{inc.last_name}, {inc.first_name}</Typography>
                        <Typography fontSize={10} color="text.secondary">{inc.admission_number}</Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{inc.class_name || '—'}</TableCell>
                      <TableCell>
                        <Typography fontSize={11}>{itLabel(inc.incident_type)}</Typography>
                        {inc.other_description && <Typography fontSize={10} color="text.secondary">→ {inc.other_description}</Typography>}
                      </TableCell>
                      <TableCell>
                        <Chip label={sev.text} size="small" color={sev.color} sx={{ fontSize: 10, fontWeight: 700 }} />
                      </TableCell>
                      <TableCell sx={{ fontSize: 11, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {acLabel(inc.action_taken)}
                      </TableCell>
                      <TableCell align="center">
                        {inc.parent_notified
                          ? <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                          : <Schedule sx={{ fontSize: 16, color: 'text.disabled' }} />}
                      </TableCell>
                      <TableCell>
                        {inc.resolved
                          ? <Chip label="Resolved" size="small" color="success" sx={{ fontSize: 10 }} />
                          : <Chip label="Open" size="small" color="warning" sx={{ fontSize: 10 }} />}
                        {inc.follow_up_required && !inc.resolved && (
                          <Chip label="Follow-up" size="small" color="error" sx={{ fontSize: 9, ml: 0.3 }} />
                        )}
                      </TableCell>
                      <TableCell onClick={e => e.stopPropagation()}>
                        <Stack direction="row" spacing={0.3}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => { setEditing(inc); setDialog(true); }}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => del(inc.id)}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Detail Dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: detail ? SEVERITY_CFG[detail.severity]?.bg : 'white', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel />
          {detail && `${itLabel(detail.incident_type)} — ${fmtDate(detail.incident_date)}`}
          <Box sx={{ flexGrow: 1 }} />
          {detail && <Chip label={SEVERITY_CFG[detail.severity]?.text} color={SEVERITY_CFG[detail.severity]?.color} size="small" />}
        </DialogTitle>
        <DialogContent dividers>
          {detail && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Student</Typography>
                <Typography fontWeight={600}>{detail.first_name} {detail.last_name}</Typography>
                <Typography fontSize={12} color="text.secondary">{detail.admission_number} · {detail.class_name}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" display="block">Logged By</Typography>
                <Typography fontSize={13}>{detail.logged_by_name || 'Unknown'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">Incident Description</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 1, bgcolor: '#fafafa', mt: 0.5 }}>
                  <Typography fontSize={13}>{detail.description}</Typography>
                </Paper>
              </Grid>
              {detail.location && <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Location</Typography><Typography fontSize={13}>{detail.location}</Typography></Grid>}
              {detail.witnesses && <Grid item xs={6}><Typography variant="caption" color="text.secondary" display="block">Witnesses</Typography><Typography fontSize={13}>{detail.witnesses}</Typography></Grid>}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">Actions Taken</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {detail.action_taken?.split(',').map(a => (
                    <Chip key={a} label={ACTIONS.find(x => x.value === a)?.label || a} size="small" color="primary" sx={{ fontSize: 10, mb: 0.3 }} />
                  ))}
                </Stack>
              </Grid>
              {(detail.suspension_days > 0) && (
                <Grid item xs={12}>
                  <Alert severity="warning" sx={{ fontSize: 12 }}>
                    Suspended for {detail.suspension_days} day(s): {fmtDate(detail.suspension_start)} — {fmtDate(detail.suspension_end)}
                  </Alert>
                </Grid>
              )}
              <Grid item xs={4}><Typography variant="caption" color="text.secondary" display="block">Parent Notified</Typography><Typography fontSize={13} color={detail.parent_notified ? 'success.main' : 'error.main'}>{detail.parent_notified ? 'Yes' : 'No'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="caption" color="text.secondary" display="block">Follow-up</Typography><Typography fontSize={13}>{detail.follow_up_required ? 'Required' : 'Not required'}</Typography></Grid>
              <Grid item xs={4}><Typography variant="caption" color="text.secondary" display="block">Status</Typography><Chip label={detail.resolved ? 'Resolved' : 'Open'} size="small" color={detail.resolved ? 'success' : 'warning'} /></Grid>
              {detail.follow_up_notes && <Grid item xs={12}><Typography variant="caption" color="text.secondary" display="block">Follow-up Notes</Typography><Typography fontSize={13}>{detail.follow_up_notes}</Typography></Grid>}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
          {detail && <Button variant="outlined" onClick={() => { setEditing(detail); setDetail(null); setDialog(true); }}>Edit</Button>}
        </DialogActions>
      </Dialog>

      <IncidentDialog open={dialog} onClose={() => { setDialog(false); setEditing(null); }} onSave={save}
        editing={editing} students={students} academicYears={academicYears} />
    </Box>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function Disciplinary() {
  const [tab, setTab]           = useState(0);
  const [academicYears, setAY]  = useState([]);
  const [classes, setClasses]   = useState([]);
  const [students, setStudents] = useState([]);

  useEffect(() => {
    settingsAPI.getAcademicYears().then(r => { if (r.data.success) setAY(r.data.data); }).catch(() => {});
    settingsAPI.getClasses().then(r => { if (r.data.success) setClasses(r.data.data); }).catch(() => {});
    api.get('/students', { params: { limit: 2000, status: 'active' } }).then(r => { if (r.data.success) setStudents(r.data.data); }).catch(() => {});
  }, []);

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gavel color="error" /> Disciplinary Records
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Incident logging · Actions · Suspension tracking · Parent visibility
        </Typography>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}>
        <Tab icon={<BarChart fontSize="small" />} label="Overview" iconPosition="start" sx={{ fontSize: 12 }} />
        <Tab icon={<Gavel fontSize="small" />} label="All Incidents" iconPosition="start" sx={{ fontSize: 12 }} />
      </Tabs>

      <TabPanel value={tab} index={0}><StatsTab academicYears={academicYears} /></TabPanel>
      <TabPanel value={tab} index={1}><IncidentsTab academicYears={academicYears} classes={classes} students={students} /></TabPanel>
    </Box>
  );
}
